import { useQuery } from '@apollo/client';
import React, { useMemo, useState, useCallback } from 'react';
import {
  FetchAddOnsParentEntitlementV2Query,
  FetchAddOnsParentEntitlementV2QueryVariables,
  EntitlementDetails,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { STATUSPAGE_PATH_2, NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { fetchAddOnsParentEntitlementV2 } from './queries/fetch-add-ons-parent-entitlement';
import {
  DynamicResultTable,
  HeadCell,
  FormattedDate,
  CustomizeView,
  FormattedAddress,
  NoDataContentInTable,
  entitlementUtils,
} from '@css-xp/base';
import { CloudProductDetailView, isActiveSubscription } from '@css-xp/base/entitlement-utils';
import { useHistory } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import VerticalMoreIcon from '@atlaskit/icon/glyph/more-vertical';
import { useTransactionAccountContext } from '@css-xp/base/common-context';
import { isTrialEntitlement } from '../../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { R300 } from '@atlaskit/theme/colors';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';

export interface AddOnsParentEntitlementProps {
  parentSen: string | undefined;
}

const AddOnsParentEntitlementTab = ({ parentSen }: AddOnsParentEntitlementProps) => {
  const { data, error, loading } = useQuery<
    FetchAddOnsParentEntitlementV2Query,
    FetchAddOnsParentEntitlementV2QueryVariables
  >(fetchAddOnsParentEntitlementV2, {
    variables: { id: parentSen },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const parentStatusPageEntitlement = data?.entitlementDetails;

  const subscriptions = useMemo(() => {
    if (data?.entitlementDetails) {
      return [data?.entitlementDetails];
    }
    return [];
  }, [data]);

  const history = useHistory();

  const { data: transactionAccountData, getTransactionAccount } = useTransactionAccountContext();
  const transactionAccount = getTransactionAccount(transactionAccountData);

  const getErrorState = React.useCallback((error: string | undefined) => {
    const renderImage = () => <WarningIcon label="Error" size="xlarge" primaryColor={R300} />;
    return {
      header: '',
      description: (
        <span className="error-view">Oh no! Something went wrong, please try again. {error && error.toString()}</span>
      ),
      renderImage,
    };
  }, []);

  const getRowAction = useCallback(
    (product: EntitlementDetails) => {
      return (
        <DropdownMenu
          position="bottom right"
          triggerType="button"
          triggerButtonProps={{
            appearance: 'subtle',
            className: 'trigger-button',
            iconBefore: <VerticalMoreIcon label="invoice-action" />,
          }}
          testId={`actions-${product?.id}`}
        >
          <DropdownItemGroup>
            <DropdownItem
              key={'add-promotion'}
              onClick={() => {
                history.push(`${STATUSPAGE_PATH_2}/${encodeURIComponent(product?.id)}`);
              }}
            >
              View details
            </DropdownItem>
          </DropdownItemGroup>
        </DropdownMenu>
      );
    },
    [history]
  );

  const getSoldToAddress = useCallback(
    (invoiceGroupId: string | undefined | null) => {
      let currentInvoiceGroupDetails;
      if (!invoiceGroupId) {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes[0];
      } else {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes?.find(
          (invoiceGroupObj) => invoiceGroupObj?.id === invoiceGroupId
        );
      }
      if (currentInvoiceGroupDetails) {
        return (
          <div className="line-clamp">
            <FormattedAddress
              addressLine1={currentInvoiceGroupDetails?.shipToParty?.addressLine1}
              addressLine2={currentInvoiceGroupDetails?.shipToParty?.addressLine2}
              city={currentInvoiceGroupDetails?.shipToParty?.city}
              country={currentInvoiceGroupDetails?.shipToParty?.country}
              postcode={currentInvoiceGroupDetails?.shipToParty?.postcode}
              state={currentInvoiceGroupDetails?.shipToParty?.state}
            />
          </div>
        );
      }
      return <NoDataContentInTable />;
    },
    [transactionAccount]
  );

  const getColumnData = useCallback(
    (header: string, product: EntitlementDetails) => {
      switch (header) {
        case 'Subscription':
          return {
            content: (
              <CloudProductDetailView
                productId={product?.productId as string}
                entitlementNumber={product?.entitlementNumber || ''}
                trial={isTrialEntitlement(product, isStatuspage(product?.productId))}
                billingStatus={product?.billingStatus}
                provisioningStatus={product?.provisioningStatus}
                edition={product?.edition}
                productName={product?.productName}
                isSandbox={product?.isSandbox ?? false}
              />
            ),
          };
        case 'Site': {
          const siteName = product?.cloudSiteHostname;
          return {
            content: siteName ? (
              <div className="line-clamp site-name-container" title={siteName}>
                {siteName}
              </div>
            ) : (
              <NoDataContentInTable />
            ),
          };
        }
        case 'Next bill/ Expiry':
          return {
            content:
              product?.latestPeriodEndDate &&
              isActiveSubscription(product?.billingStatus, product?.provisioningStatus) ? (
                <FormattedDate date={product?.latestPeriodEndDate} format={NEW_DATE_FORMAT} />
              ) : (
                <NoDataContentInTable />
              ),
          };
        case 'Sold-to address':
          return {
            content: getSoldToAddress(product?.billingGroupId),
          };
        case 'Payment method':
          return {
            content: (
              <entitlementUtils.PaymentMethodView
                invoiceGroupId={product?.billingGroupId}
                transactionAccount={transactionAccount as TransactionAccount}
              />
            ),
          };
        default:
          return {
            content: product?.billingCycle ? capitalize(product.billingCycle) : <NoDataContentInTable />,
          };
      }
    },
    [getSoldToAddress, transactionAccount]
  );

  const headData = useMemo(() => {
    const cells = [
      {
        content: 'Subscription',
        width: 25,
      },
      {
        content: 'Site',
        width: 20,
        isMandatory: false,
        isChecked: true,
      },
      {
        content: 'Next bill/ Expiry',
        width: 15,
      },
      {
        content: 'Billing cycle',
        width: 20,
        isMandatory: false,
        isChecked: true,
      },
      {
        content: 'Payment method',
        width: 20,
        isMandatory: false,
        isChecked: true,
      },
      {
        content: 'Sold-to address',
        width: 20,
        isMandatory: false,
        isChecked: true,
      },
    ];

    return {
      cells,
    };
  }, []);

  const [columnsList, setColumnList] = useState<{ cells: Array<HeadCell> }>(headData);

  const selectedColumns = useMemo(() => {
    const columns = columnsList.cells.filter(
      (column) => column.isChecked || !Object.prototype.hasOwnProperty.call(column, 'isChecked')
    );
    return { cells: columns };
  }, [columnsList]);

  const rowData = useMemo(() => {
    return (subscriptions ?? []).map(
      //@ts-ignore
      (product: EntitlementDetails) => {
        return {
          key: product?.id,
          cells: [
            ...selectedColumns.cells.map((columnData) => getColumnData(columnData.content as string, product)),
            {
              content: getRowAction(product),
            },
          ],
        };
      }
    );
  }, [subscriptions, getColumnData, selectedColumns, getRowAction]);

  return (
    <DynamicResultTable
      headingText={'Parent subscription'}
      head={{
        cells: [
          ...selectedColumns.cells,
          {
            content: '',
            width: 2,
          },
        ],
      }}
      rows={rowData}
      emptyStateProps={{
        header: '',
        description: NO_DETAILS_TO_SHOW,
      }}
      error={error?.message}
      errorStateProps={getErrorState(error?.message)}
      isLoading={loading}
      totalCount={parentStatusPageEntitlement ? 1 : 0}
      isPaginationEnabled={false}
      customiseColumnView={
        <CustomizeView
          columns={columnsList.cells}
          updatedColumnView={(record: Array<HeadCell>) => setColumnList({ cells: [...record] })}
        />
      }
    ></DynamicResultTable>
  );
};

export default AddOnsParentEntitlementTab;
